/* Blissal TR — small, dependency-free UI primitives shared across sections. */

/* Rotates [data-slide] children; optional prev/next buttons and generated dots. */
class BlRotator extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll(':scope [data-slide]')].filter((s) => s.closest('bl-rotator') === this);
    if (this.slides.length < 2) return;
    this.index = 0;
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
    const dotsContainer = this.querySelector('[data-dots-container]');
    if (dotsContainer) {
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `${i + 1}`);
        dot.addEventListener('click', () => this.go(i, true));
        dotsContainer.appendChild(dot);
        return dot;
      });
    }
    this.render();
    const seconds = Number(this.dataset.interval || 0);
    if (seconds > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.timer = setInterval(() => this.go(this.index + 1), seconds * 1000);
    }
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  go(target, userInitiated = false) {
    if (userInitiated) clearInterval(this.timer);
    this.index = (target + this.slides.length) % this.slides.length;
    this.render();
  }

  render() {
    this.slides.forEach((slide, i) => (slide.hidden = i !== this.index));
    this.dots?.forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
    this.dispatchEvent(new CustomEvent('bl:slide', { detail: { index: this.index } }));
  }
}
customElements.define('bl-rotator', BlRotator);

/* Off-canvas drawer opened by any [data-drawer-open="<id>"] trigger. */
class BlDrawer extends HTMLElement {
  connectedCallback() {
    document.querySelectorAll(`[data-drawer-open="${this.id}"]`).forEach((trigger) =>
      trigger.addEventListener('click', () => this.open(trigger))
    );
    this.querySelectorAll('[data-drawer-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.addEventListener('keyup', (event) => event.key === 'Escape' && this.close());
  }

  open(trigger) {
    this.trigger = trigger;
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
    this.querySelector('[data-drawer-close]:not(.bl-drawer__overlay)')?.focus();
  }

  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
    this.trigger?.focus();
  }
}
customElements.define('bl-drawer', BlDrawer);

/* Horizontal scroll-snap track with prev/next buttons and optional dots. */
class BlCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.items = [...this.track.children];
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.step(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.step(1));
    const dotsContainer = this.querySelector('[data-dots-container]');
    if (dotsContainer && this.items.length > 1) {
      this.dots = this.items.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.setAttribute('aria-label', `${i + 1}`);
        dot.addEventListener('click', () => this.scrollToIndex(i));
        dotsContainer.appendChild(dot);
        return dot;
      });
    }
    this.track.addEventListener('scroll', () => requestAnimationFrame(() => this.sync()), { passive: true });
    this.sync();
  }

  currentIndex() {
    const left = this.track.scrollLeft;
    let closest = 0;
    this.items.forEach((item, i) => {
      if (Math.abs(item.offsetLeft - this.track.offsetLeft - left) < Math.abs(this.items[closest].offsetLeft - this.track.offsetLeft - left)) closest = i;
    });
    return closest;
  }

  step(direction) {
    const next = this.currentIndex() + direction;
    if (this.hasAttribute('loop')) {
      this.scrollToIndex((next + this.items.length) % this.items.length);
    } else {
      this.scrollToIndex(Math.max(0, Math.min(this.items.length - 1, next)));
    }
  }

  scrollToIndex(i) {
    this.track.scrollTo({ left: this.items[i].offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  sync() {
    const index = this.currentIndex();
    this.dots?.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
    this.dispatchEvent(new CustomEvent('bl:slide', { detail: { index } }));
  }
}
customElements.define('bl-carousel', BlCarousel);

/* Tabs: [data-tab] buttons toggle matching [data-panel] by index. Pauses videos in hidden panels. */
class BlTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[data-tab]')];
    this.panels = [...this.querySelectorAll('[data-panel]')];
    this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.select(i)));
  }

  select(index) {
    this.tabs.forEach((tab, i) => tab.setAttribute('aria-selected', String(i === index)));
    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
      const video = panel.querySelector('video');
      if (!video) return;
      if (i === index) video.play().catch(() => {});
      else video.pause();
    });
  }
}
customElements.define('bl-tabs', BlTabs);

/* Lazy autoplay for muted background videos: only plays while visible. */
const blVideoObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        if (isIntersecting) {
          if (!target.src && target.dataset.src) target.src = target.dataset.src;
          if (!target.closest('[hidden]')) target.play().catch(() => {});
        } else {
          target.pause();
        }
      });
    }, { rootMargin: '200px' })
  : null;

function blObserveVideos(root = document) {
  root.querySelectorAll('video[data-bl-autoplay]').forEach((video) => {
    if (blVideoObserver) blVideoObserver.observe(video);
    else if (video.dataset.src) video.src = video.dataset.src;
  });
}
blObserveVideos();
document.addEventListener('shopify:section:load', (event) => blObserveVideos(event.target));
